import { useState, useEffect } from 'react';
import { signInWithPopup, signOut, onAuthStateChanged, User as FirebaseUser } from 'firebase/auth';
import { auth, googleProvider } from '../firebase/config';

export interface User {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  provider: 'google';
}

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Listen to Firebase auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser: FirebaseUser | null) => {
      console.log('Auth state changed:', firebaseUser ? 'User logged in' : 'User logged out');
      
      if (firebaseUser) {
        // Convert Firebase user to our User interface
        const user: User = {
          id: firebaseUser.uid,
          name: firebaseUser.displayName || 'User',
          email: firebaseUser.email || '',
          avatar: firebaseUser.photoURL || undefined,
          provider: 'google'
        };
        setUser(user);
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = async (provider: 'google') => {
    setLoading(true);
    
    try {
      await signInWithPopup(auth, googleProvider);
      // User state will be updated automatically by onAuthStateChanged
    } catch (error) {
      console.error('Authentication failed:', error);
      setLoading(false);
    }
  };

  const logout = async () => {
    setLoading(true);
    try {
      console.log('Starting logout process...');
      await signOut(auth);
      console.log('Firebase signOut completed successfully');
      // User state will be updated automatically by onAuthStateChanged
    } catch (error) {
      console.error('Logout failed:', error);
      setLoading(false);
    }
  };

  return {
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user
  };
};